import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { toast } from 'react-hot-toast';
import { Download, RefreshCw } from 'react-feather';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { ConfigPageShell, ScopeSelect, ToolbarButton } from '../components/ConfigPages';
import { InlineSelect } from '../components/InlineTable';
import { EtlBanner } from '../components/EtlPages';
import useEtlScope, { ALL_ORGANIZATIONS } from './etlConfig/useEtlScope';
import StatusTab from './etlConfig/StatusTab';
import FeedsTab from './etlConfig/FeedsTab';
import UploadTab from './etlConfig/UploadTab';
import ReviewTab from './etlConfig/ReviewTab';
import SchemaWizard from './etlConfig/SchemaWizard';
import DlqSection from '../components/FileTracking/DlqSection';
import DocumentsSection from '../components/FileTracking/DocumentsSection';
import UnmappedCodesSection from '../components/FileTracking/UnmappedCodesSection';
import FileViewerModal from '../components/FileViewerModal';
import FileTrackingEmailModal from '../components/FileTracking/FileTrackingEmailModal';
import FeedOptionsModal from '../components/FileTracking/FeedOptionsModal';
import { detectFileViewerType, getFileNameFromPath } from '../utils/fileViewer';
import { fetchImportPdf, getEtlConfigForHotel } from '../utils/api';
import { useIsMobile } from '../hooks/use-mobile';
// Phone layout for the head band (scrolling tab strip, 2×2 flow summary).
import '../components/EtlPages/etlMobile.css';

/**
 * ETL Configuration — one page, one scope, one visual grammar.
 *
 * This replaces four sibling pages (File Tracking, Data Import, Import Review,
 * Schema Builder) that each carried their own header, their own organization
 * picker, their own search box and their own status colours. They were always
 * one workflow — configure a feed, watch it land, fix what didn't — so they
 * are one page with tabs now, built on the same ConfigPages grammar as Chart of
 * Accounts and the other mapping pages.
 *
 * The schema wizard is the exception: it takes the page over (breadcrumb bar
 * instead of the tab strip) because a six-step flow inside a tab strip invites
 * you to navigate away mid-edit.
 */

const TAB_ORDER = ['status', 'issues', 'documents', 'unmapped', 'review', 'feeds', 'upload'];

const TAB_META = {
  status:    { label: 'Status',    subtitle: 'Which expected files have landed today, and which have not.' },
  issues:    { label: 'Issues',    subtitle: 'Files that failed to process, with the payload and the error that stopped them.' },
  documents: { label: 'Documents', subtitle: 'Every source document we have received, and what we extracted from it.' },
  unmapped:  { label: 'Unmapped',  subtitle: 'Codes that arrived in a feed with nowhere to land until someone maps them.' },
  review:    { label: 'Review',    subtitle: 'Compare what a source file said against what we imported.' },
  feeds:     { label: 'Feeds',     subtitle: 'Every configured integration: what we match on, how it is extracted, when it last ran.' },
  upload:    { label: 'Upload',    subtitle: 'Send a file through the pipeline by hand — backfills, re-runs and one-offs.' },
};

export default function EtlConfigPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { tab: tabParam, hotelId: hotelIdParam } = useParams();
  const { userSettings: currentUser, currentOrganization } = useUserSettingsContext();

  const importReviewEnabled = useFeatureFlagEnabled('import-review');
  const schemaBuilderEnabled = useFeatureFlagEnabled('schema-builder');

  // `/etl-config/schema[/:hotelId]` is the wizard takeover, not a tab.
  const inWizard = routerLocation.pathname.includes('/etl-config/schema');
  const activeTab = TAB_ORDER.includes(tabParam) ? tabParam : 'status';
  const isMobile = useIsMobile();

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const scope = useEtlScope(currentUser, currentOrganization, refreshTrigger);

  // Tabs hand the shell their refresh/export implementations so the header
  // buttons stay in one place instead of being redrawn per view.
  const refreshRef = useRef(null);
  const exportRef = useRef(null);
  const registerRefresh = useCallback((fn) => { refreshRef.current = fn; }, []);
  const registerExport = useCallback((fn) => { exportRef.current = fn; }, []);
  useEffect(() => { refreshRef.current = null; exportRef.current = null; }, [activeTab]);

  const [todayCounts, setTodayCounts] = useState(null);
  const [feedCount, setFeedCount] = useState(null);
  const [unmappedCount, setUnmappedCount] = useState(null);

  // Shared modals — every tab can open the file viewer, the alert email
  // composer and the tracking-schedule editor, so they live on the page.
  const [viewer, setViewer] = useState(null); // { url, fileType, fileName }
  const [emailRecord, setEmailRecord] = useState(null);
  const [trackingConfig, setTrackingConfig] = useState(null);

  const goTab = useCallback((key) => {
    navigate(`/etl-config/${key}${routerLocation.search}`);
  }, [navigate, routerLocation.search]);

  // Land on a real tab when the URL has none or names one the user can't see.
  useEffect(() => {
    if (inWizard) return;
    if (!TAB_ORDER.includes(tabParam) || (tabParam === 'review' && importReviewEnabled === false)) {
      navigate(`/etl-config/status${routerLocation.search}`, { replace: true });
    }
  }, [inWizard, tabParam, importReviewEnabled, navigate, routerLocation.search]);

  // The sidebar's "Issues" shortcut passes its intent through router state.
  useEffect(() => {
    if (routerLocation.state?.etlDefaultTab) {
      const target = routerLocation.state.etlDefaultTab;
      navigate(`/etl-config/${TAB_ORDER.includes(target) ? target : 'status'}`, { replace: true, state: {} });
    }
  }, [routerLocation.state?.etlDefaultTab, navigate]);

  const handleViewFile = useCallback(async (record) => {
    const fileUrl = record?.reception?.blobUrl || record?.tracking?.lastProcessedFile || record?.blobUrl;
    if (!fileUrl) { toast.error('No file available to view'); return; }
    try {
      const blobUrl = await fetchImportPdf(fileUrl);
      setViewer({
        url: blobUrl,
        fileType: detectFileViewerType(fileUrl, 'pdf'),
        fileName: getFileNameFromPath(fileUrl),
      });
    } catch (err) {
      console.error('Error fetching file:', err);
      toast.error('Failed to load file');
    }
  }, []);

  const handleConfigureTracking = useCallback(async (record) => {
    try {
      const etlConfig = await getEtlConfigForHotel(record.hotelId);
      const fileConfig = etlConfig?.fileConfigs?.find((fc) => fc.fileType === record.fileType);
      setTrackingConfig({
        hotelId: record.hotelId,
        hotelCode: record.hotelCode || etlConfig?.hotelCode,
        fileType: record.fileType,
        displayName: record.displayName,
        locationName: record.metadata?.locationName || record.locationName,
        auditFile: fileConfig?.auditFile,
        emailMatchType: fileConfig?.emailMatchType,
        tracking: fileConfig?.tracking || {},
      });
    } catch (err) {
      console.error('Error loading config for tracking:', err);
      toast.error('Failed to load tracking configuration');
    }
  }, []);

  const bumpRefresh = () => setRefreshTrigger((n) => n + 1);

  // Phones: the tab strip scrolls sideways, so bring the active tab into view
  // when it changes (deep links land on tabs that start off-screen).
  useEffect(() => {
    if (!isMobile || inWizard) return;
    // Scroll the strip itself rather than scrollIntoView, which can also
    // move the page vertically.
    const el = document.querySelector('.cp-page.ep-etl .cp-tabs [aria-selected="true"]');
    const strip = el?.parentElement;
    if (!el || !strip) return;
    const a = el.getBoundingClientRect();
    const b = strip.getBoundingClientRect();
    strip.scrollLeft += (a.left - b.left) - (b.width - a.width) / 2;
  }, [isMobile, inWizard, activeTab]);

  const tabs = useMemo(() => TAB_ORDER
    .filter((key) => (key === 'review' ? importReviewEnabled !== false : true))
    .map((key) => {
      const meta = { key, label: TAB_META[key].label };
      if (key === 'issues' && scope.dlqBadgeCount > 0) return { ...meta, count: scope.dlqBadgeCount, warn: true };
      if (key === 'unmapped' && unmappedCount > 0) return { ...meta, count: unmappedCount, warn: true };
      if (key === 'feeds' && feedCount != null) return { ...meta, count: feedCount };
      return meta;
    }), [importReviewEnabled, scope.dlqBadgeCount, unmappedCount, feedCount]);

  const orgOptions = useMemo(() => ([
    ...(scope.availableOrgs.length > 1 ? [{ value: ALL_ORGANIZATIONS, label: 'All organizations' }] : []),
    ...scope.availableOrgs.map((o) => ({ value: o.code, label: o.name || o.code })),
  ]), [scope.availableOrgs]);

  /**
   * The flow rail — the same "direction is the content" device the mapping
   * pages use, pointed at the pipeline: a file arrives, gets extracted, gets
   * normalised, and either lands or needs a human.
   */
  const flow = useMemo(() => {
    if (!todayCounts) return null;
    const expected = todayCounts.all || 0;
    const landed = todayCounts.processed || 0;
    const attention = (todayCounts.failed || 0) + (todayCounts.missed || 0) + (todayCounts.needs_review || 0);
    return {
      stations: [
        { label: 'Expected today', value: expected.toLocaleString(), sub: feedCount != null ? `${feedCount} feeds configured` : undefined },
        { label: 'In flight', value: (todayCounts.pending || 0).toLocaleString(), tone: 'muted', sub: 'received, not yet normalised' },
        { label: 'Landed', value: landed.toLocaleString(), tone: landed > 0 ? 'ok' : 'neutral', sub: 'rows written' },
        {
          label: 'Needs attention',
          value: attention.toLocaleString(),
          tone: attention > 0 ? 'warn' : 'neutral',
          sub: attention > 0 ? 'failed, missing or flagged' : 'nothing outstanding',
          onClick: attention > 0 ? () => goTab('status') : undefined,
        },
      ],
      coverage: expected > 0 ? { mapped: landed, total: expected, label: 'files landed today' } : null,
    };
  }, [todayCounts, feedCount, goTab]);

  // ── Wizard takeover ──────────────────────────────────────────
  if (inWizard) {
    if (schemaBuilderEnabled === false) {
      return (
        <div className="cp-page">
          <EtlBanner tone="warn" title="Schema Builder is not enabled">
            Ask your administrator to turn on the <code>schema-builder</code> feature for this account.
          </EtlBanner>
          <div style={{ marginTop: 12 }}>
            <ToolbarButton onClick={() => goTab('feeds')}>Back to Feeds</ToolbarButton>
          </div>
        </div>
      );
    }
    return (
      <SchemaWizard
        hotelId={hotelIdParam}
        scope={scope}
        onExit={() => navigate(`/etl-config/feeds${routerLocation.search}`)}
      />
    );
  }

  const headActions = (
    <>
      <ToolbarButton
        icon={Download}
        iconOnly
        title="Export this view as CSV"
        aria-label="Export CSV"
        onClick={() => (exportRef.current ? exportRef.current() : toast.error('Nothing to export from this tab'))}
      />
      <ToolbarButton
        variant="primary"
        icon={RefreshCw}
        iconOnly
        title="Refresh"
        aria-label="Refresh"
        onClick={() => { bumpRefresh(); if (refreshRef.current) refreshRef.current(); }}
      />
    </>
  );

  return (
    <ConfigPageShell
      title="ETL Configuration"
      subtitle={TAB_META[activeTab].subtitle}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={goTab}
      scope={orgOptions.length > 1 ? (
        <ScopeSelect label="Organization">
          <InlineSelect
            rowActive
            clearable={false}
            value={scope.selectedOrgCode}
            options={orgOptions}
            onChange={(v) => v && scope.setOrgCode(v)}
            placeholder="Choose…"
            minWidth={200}
            ariaLabel="Organization"
          />
        </ScopeSelect>
      ) : null}
      actions={headActions}
      flow={activeTab === 'status' ? flow : null}
      className="ep-etl"
    >
      {scope.error ? <EtlBanner tone="error">{scope.error}</EtlBanner> : null}

      {activeTab === 'status' && (
        <StatusTab
          currentUser={currentUser}
          scope={scope}
          refreshTrigger={refreshTrigger}
          onConfigureTracking={handleConfigureTracking}
          onViewFile={handleViewFile}
          onEmailRecord={setEmailRecord}
          onRecordsChange={setTodayCounts}
          registerRefresh={registerRefresh}
          registerExport={registerExport}
        />
      )}

      {activeTab === 'issues' && (
        <DlqSection
          scopeReady={scope.scopeReady}
          currentUser={currentUser}
          currentOrganization={currentOrganization}
          scopeOrgId={scope.orgIdForScope}
          accessibleHotelIds={scope.accessibleHotelIds}
          locations={scope.locations}
          dlqStats={scope.dlqStats}
          onStatsRefresh={scope.loadDlqStats}
          refreshTrigger={refreshTrigger}
        />
      )}

      {activeTab === 'documents' && (
        <DocumentsSection
          scopeReady={scope.scopeReady}
          currentUser={currentUser}
          accessibleHotelIds={scope.accessibleHotelIds}
          locations={scope.locations}
        />
      )}

      {activeTab === 'unmapped' && (
        <UnmappedCodesSection
          scopeReady={scope.scopeReady}
          currentOrganization={currentOrganization}
          accessibleHotelIds={scope.accessibleHotelIds}
          locations={scope.locations}
          onCountChange={setUnmappedCount}
        />
      )}

      {activeTab === 'review' && (
        <ReviewTab scope={scope} currentUser={currentUser} currentOrganization={currentOrganization} />
      )}

      {activeTab === 'feeds' && (
        <FeedsTab
          scope={scope}
          refreshTrigger={refreshTrigger}
          schemaBuilderEnabled={schemaBuilderEnabled !== false}
          onCountChange={setFeedCount}
          onConfigureTracking={handleConfigureTracking}
          registerRefresh={registerRefresh}
        />
      )}

      {activeTab === 'upload' && (
        <UploadTab
          scope={scope}
          schemaBuilderEnabled={schemaBuilderEnabled !== false}
          onUploaded={bumpRefresh}
        />
      )}

      {viewer ? (
        <FileViewerModal
          url={viewer.url}
          fileType={viewer.fileType}
          fileName={viewer.fileName}
          onClose={() => {
            if (viewer.url.startsWith('blob:')) URL.revokeObjectURL(viewer.url);
            setViewer(null);
          }}
        />
      ) : null}

      <FileTrackingEmailModal
        isOpen={!!emailRecord}
        onClose={() => setEmailRecord(null)}
        records={emailRecord ? [emailRecord] : []}
        effectiveDate={emailRecord?.effectiveDate}
        locations={scope.locations}
        currentOrganization={currentOrganization}
        singleFileMode
      />

      {trackingConfig ? (
        <FeedOptionsModal
          config={trackingConfig}
          currentOrganization={currentOrganization}
          onSave={() => { setTrackingConfig(null); bumpRefresh(); }}
          onCancel={() => setTrackingConfig(null)}
        />
      ) : null}
    </ConfigPageShell>
  );
}
